// Zero-dependency IndexedDB wrapper. Now a full local MIRROR of the last
// 30 days of Counter Memo entries — both ones created offline (origin
// 'offline') and ones synced down from the server (origin 'online') — not
// just a pending-upload queue. This is what makes a single merged list,
// with online/offline markers, possible.

const DB_NAME = 'minierp_offline';
// const DB_VERSION = 1;
const DB_VERSION = 2;
const STORE = 'petty_mirror';
const THIRTY_DAYS_MS = 30 * 86400000;

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'clientId' });
        store.createIndex('userId', 'userId', { unique: false });
        store.createIndex('serverId', 'serverId', { unique: false });
      }
    };
    // req.onsuccess = () => resolve(req.result);
    req.onsuccess = () => {
      const db = req.result;

      if (!db.objectStoreNames.contains(STORE)) {
        db.close();
        indexedDB.deleteDatabase(DB_NAME);

        dbPromise = null;

        return reject(
          new Error(
            `IndexedDB store '${STORE}' not found. Reload once after DB recreation.`
          )
        );
      }

      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
};

const withStore = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const result = fn(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
  });
};

const genClientId = () => `off-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const miniERPOfflineDB = {
  isSupported: typeof window !== 'undefined' && !!window.indexedDB,

  // A new entry created ON this device, not yet confirmed on the server.
  async addOfflineEntry(userId, payload) {
    const record = {
      clientId: genClientId(),
      userId,
      origin: 'offline',
      syncStatus: 'pending', // 'pending' | 'syncing' | 'synced' | 'failed'
      syncError: '',
      serverId: '',
      payload,
      createdAt: new Date().toISOString(),
    };
    await withStore('readwrite', (store) => store.put(record));
    return record;
  },

  // A record that already exists on the server — either it was just
  // created live (online path), or pulled down via "Download from Online."
  async upsertOnlineSnapshot(userId, serverRecord) {
    const record = {
      clientId: `srv-${serverRecord._id}`,
      userId,
      origin: 'online',
      syncStatus: 'synced',
      syncError: '',
      serverId: serverRecord._id,
      payload: serverRecord,
      createdAt: serverRecord.date || serverRecord.createdAt || new Date().toISOString(),
    };
    await withStore('readwrite', (store) => store.put(record));
    return record;
  },

  async markStatus(clientId, changes) {
    return withStore('readwrite', (store) => {
      const getReq = store.get(clientId);
      getReq.onsuccess = () => {
        if (getReq.result) store.put({ ...getReq.result, ...changes });
      };
    });
  },

  async getAllForUser(userId, { last30DaysOnly = true } = {}) {
    const db = await openDb();
    const rows = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const idx = tx.objectStore(STORE).index('userId');
      const req = idx.getAll(userId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    const cutoff = Date.now() - THIRTY_DAYS_MS;
    const filtered = last30DaysOnly
      ? rows.filter((r) => new Date(r.payload?.date || r.createdAt).getTime() >= cutoff)
      : rows;
    return filtered.sort((a, b) => new Date(b.payload?.date || b.createdAt) - new Date(a.payload?.date || a.createdAt));
  },

  async getKnownServerIds(userId) {
    const rows = await this.getAllForUser(userId, { last30DaysOnly: false });
    return new Set(rows.filter((r) => r.serverId).map((r) => r.serverId));
  },

  // Deletes every LOCAL record for this user only — never touches the server.
  async clearAllForUser(userId) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const idx = tx.objectStore(STORE).index('userId');
      const req = idx.openCursor(IDBKeyRange.only(userId));
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return resolve();
        cursor.delete();
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    });
  },

  // Bounds local storage growth: drop SYNCED records older than 30 days.
  // Never touches anything still 'pending'/'failed' — that data only exists
  // on this device until it's actually uploaded.
  async pruneOldSynced(userId) {
    const rows = await this.getAllForUser(userId, { last30DaysOnly: false });
    const cutoff = Date.now() - THIRTY_DAYS_MS;
    const stale = rows.filter((r) => r.syncStatus === 'synced' && new Date(r.payload?.date || r.createdAt).getTime() < cutoff);
    await withStore('readwrite', (store) => { stale.forEach((r) => store.delete(r.clientId)); });
  },
};